import React, { useCallback, useState, useEffect, useMemo } from 'react';
import {
  CommandSelect,
  CommandSelectTrigger,
  CommandSelectValue,
  CommandSelectContent,
  CommandSelectItem,
  CommandSelectEmpty,
  useCommandSelectSearch,
} from './command-select';

// The available network string literals. This is duplicated from filters-form/index.tsx
// to avoid introducing a runtime circular dependency. Keep in sync with networkOptions.
export type NetworkValue = 'INSTAGRAM' | 'TIKTOK' | 'PINTEREST' | 'YOUTUBE';

export interface NetworksSelectProps {
  children: React.ReactNode;
}

export const NetworksSelect: React.FC<NetworksSelectProps> = ({ children }) => {
  return (
    <div className="relative">
      {children}
    </div>
  );
};

export const NetworksOptions = ({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: NetworkValue; label: string }>;
  value: NetworkValue[];
  onChange: (values: NetworkValue[]) => void;
}) => {
  const [localValue, setLocalValue] = useState<NetworkValue[]>(value);
  const [isUpdating, setIsUpdating] = useState(false);

  // Sync local state with prop value
  useEffect(() => {
    if (!isUpdating) {
      // Only update if arrays are actually different (deep equality check)
      const arraysAreEqual = localValue.length === value.length &&
        localValue.every((val, index) => val === value[index]);

      if (!arraysAreEqual) {
        setLocalValue(value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isUpdating]); // localValue intentionally excluded to prevent infinite loop

  // Unified update handler with proper state management
  const updateSelection = useCallback(
    (newSelection: NetworkValue[]) => {
      setIsUpdating(true);
      setLocalValue(newSelection);
      onChange(newSelection);

      // Reset updating flag after state has settled
      setTimeout(() => {
        setIsUpdating(false);
      }, 0);
    },
    [onChange],
  );

  const handleDismiss = useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();
      updateSelection([]);
    },
    [updateSelection],
  );

  // Display text for badge
  const getDisplayText = () => {
    if (localValue.length === 0) return { mainText: 'Select networks...', count: null };
    if (localValue.length === 1) {
      const network = options.find((opt) => opt.value === localValue[0]);
      return { mainText: network?.label || localValue[0], count: null };
    }
    const firstNetwork = options.find((opt) => opt.value === localValue[0]);
    return {
      mainText: firstNetwork?.label || localValue[0],
      count: localValue.length - 1
    };
  };

  return (
    <CommandSelect>
      <CommandSelectTrigger placeholder="Select networks...">
        <CommandSelectValue
          placeholder="Select networks..."
          displayText={localValue.length > 0 ? getDisplayText().mainText : undefined}
          count={getDisplayText().count}
          onClear={handleDismiss}
          disabled={isUpdating}
        />
      </CommandSelectTrigger>
      <CommandSelectContent searchPlaceholder="Search networks..." maxHeight="200px">
        <NetworksContentWithSearch
          options={options}
          localValue={localValue}
          isUpdating={isUpdating}
          updateSelection={updateSelection}
        />
      </CommandSelectContent>
    </CommandSelect>
  );
};

// Component that uses the search context for filtering
const NetworksContentWithSearch = ({
  options,
  localValue,
  isUpdating,
  updateSelection,
}: {
  options: Array<{ value: NetworkValue; label: string }>;
  localValue: NetworkValue[];
  isUpdating: boolean;
  updateSelection: (newSelection: NetworkValue[]) => void;
}) => {
  // Get the search query from the CommandSelect context
  const searchQuery = useCommandSelectSearch();

  // Get filtered networks based on search query from context
  const filteredNetworks = useMemo(() => {
    if (!searchQuery) return options;
    const query = searchQuery.toLowerCase();
    return options.filter((option) => option.label.toLowerCase().includes(query));
  }, [options, searchQuery]);

  const toggleNetwork = useCallback((network: NetworkValue) => {
    if (isUpdating) return;

    const newValue = localValue.includes(network)
      ? localValue.filter((v) => v !== network)
      : [...localValue, network];

    updateSelection(newValue);
  }, [localValue, updateSelection, isUpdating]);

  return (
    <>
      {filteredNetworks.length > 0 ? (
        filteredNetworks.map(({ value: networkValue, label: networkLabel }) => {
          const isSelected = localValue.includes(networkValue);

          return (
            <CommandSelectItem
              key={networkValue}
              value={networkValue}
              onSelect={() => toggleNetwork(networkValue)}
              selected={isSelected}
              disabled={isUpdating}
              className="pl-4"
            >
              <span>{networkLabel}</span>
            </CommandSelectItem>
          );
        })
      ) : (
        <CommandSelectEmpty>No networks found.</CommandSelectEmpty>
      )}
    </>
  );
};
